import { createSlice } from '@reduxjs/toolkit';
import { LOADING_STATUS } from '../../../shared/constants/loadingStatus';
import { resolveTabKey } from './lib/resolveTabKey';
import { CHANNEL } from '../constants/messageTemplateKeys';
import { createListTabState } from './lib/createTabState';
import {
  templatesFiltersChanged,
  templatesOrderingChanged,
  templatesSearchChanged,
  templatesTagsModeChanged,
} from './templatesQuerySlice';

const initialState = {
  tabs: {
    email: createListTabState(),
    text: createListTabState(),
  },
};

function resetAllTabPages(state) {
  for (const key of Object.keys(state.tabs)) {
    state.tabs[key].page = 1;
    state.tabs[key].listStatus = LOADING_STATUS.IDLE;
    // A reset query invalidates whatever is in flight for the old one.
    state.tabs[key].listRequestId = null;
  }
}

export const templatesListSlice = createSlice({
  name: 'templatesList',
  initialState,
  reducers: {
    templatesTabPageChanged(state, action) {
      const { tabKey, page } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const nextPage = Number(page) || 1;
      const tab = state.tabs?.[resolved] ?? createListTabState();
      tab.page = Math.max(1, nextPage);
      tab.listStatus = LOADING_STATUS.IDLE;
      tab.listRequestId = null;
      state.tabs[resolved] = tab;
    },
    templatesTabPageSizeChanged(state, action) {
      const { tabKey, pageSize } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const nextPageSize = Number(pageSize);
      if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) return;
      const tab = state.tabs?.[resolved] ?? createListTabState();
      tab.pageSize = nextPageSize;
      tab.page = 1;
      tab.listStatus = LOADING_STATUS.IDLE;
      tab.listRequestId = null;
      state.tabs[resolved] = tab;
    },
    templatesListRequested(state, action) {
      const { tabKey, requestId } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey ?? CHANNEL.EMAIL);
      const tab = state.tabs?.[resolved] ?? createListTabState();
      tab.listStatus = LOADING_STATUS.LOADING;
      tab.listRequestId = requestId ?? null;
      state.tabs[resolved] = tab;
    },
    templatesListReceived(state, action) {
      const { tabKey, requestId, payload } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const tab = state.tabs?.[resolved] ?? createListTabState();
      // Search fires a request per keystroke and responses can land out of order — only the
      // latest request may write, or a stale result overwrites the current one.
      if (requestId !== tab.listRequestId) return;
      tab.listStatus = LOADING_STATUS.SUCCEEDED;
      tab.items = payload?.results ?? [];
      tab.count = payload?.count ?? 0;
      tab.totalPages = payload?.total_pages ?? 1;
      state.tabs[resolved] = tab;
    },
    templatesListFailed(state, action) {
      const { tabKey, requestId } = action.payload ?? {};
      const resolved = resolveTabKey(tabKey);
      const tab = state.tabs?.[resolved] ?? createListTabState();
      if (requestId !== tab.listRequestId) return;
      tab.listStatus = LOADING_STATUS.FAILED;
      state.tabs[resolved] = tab;
    },
    templatesListResetAll(state) {
      resetAllTabPages(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(templatesSearchChanged, resetAllTabPages)
      .addCase(templatesOrderingChanged, resetAllTabPages)
      .addCase(templatesFiltersChanged, resetAllTabPages)
      .addCase(templatesTagsModeChanged, resetAllTabPages);
  },
});

export const templatesListReducer = templatesListSlice.reducer;

export const {
  templatesTabPageChanged,
  templatesTabPageSizeChanged,
  templatesListRequested,
  templatesListReceived,
  templatesListFailed,
  templatesListResetAll,
} = templatesListSlice.actions;

export const selectTemplatesListTabs = (state) => state.templates.list.tabs;

function selectActiveTabKey(state) {
  return state.templates.query.activeTab ?? CHANNEL.EMAIL;
}

function selectActiveTabState(state) {
  const tabKey = selectActiveTabKey(state);
  return state.templates.list.tabs?.[tabKey] ?? createListTabState();
}

export const selectTemplatesListStatus = (state) => selectActiveTabState(state).listStatus;
export const selectTemplatesItems = (state) => selectActiveTabState(state).items;
export const selectTemplatesPage = (state) => selectActiveTabState(state).page;
export const selectTemplatesPageSize = (state) => selectActiveTabState(state).pageSize;
export const selectTemplatesTotalPages = (state) => selectActiveTabState(state).totalPages;
